export interface LiveReadinessGap {
    status: 'notAvailableLive' | 'temporarilyUnavailable';
    code: string | null;
    reason: string;
}

export interface LiveReadinessCallout {
    title: string;
    variant: 'error' | 'warning';
    reason: string;
    guidance: string;
    /** Only a transient condition is worth checking again; a permanent gap never clears by waiting. */
    shouldRecheck: boolean;
    /** The attached version itself is out of date: only republishing the rules clears the gap. */
    requiresRepublish: boolean;
}

/** The runtime no longer serves the context feature versions the attached version was compiled against. */
const LIVE_CONTEXT_FEATURE_INCOMPATIBLE = 'LIVE_CONTEXT_FEATURE_INCOMPATIBLE';

export const LIVE_READINESS_RECHECK_INTERVAL_MS = 15_000;

export const describeLiveReadinessCallout = (gap: LiveReadinessGap): LiveReadinessCallout => {
    if (gap.status === 'temporarilyUnavailable') {
        return {
            title: 'Live runtime temporarily unavailable',
            variant: 'warning',
            reason: gap.reason,
            guidance: 'This usually clears within a few minutes. The check repeats automatically.',
            shouldRecheck: true,
            requiresRepublish: false
        };
    }

    if (gap.code === LIVE_CONTEXT_FEATURE_INCOMPATIBLE) {
        return {
            title: 'Not available live',
            variant: 'error',
            reason: gap.reason,
            guidance:
                'This will not change by waiting. Republishing compiles the rules against the current features; ' +
                'backtests of this version also fail until then.',
            shouldRecheck: false,
            requiresRepublish: true
        };
    }

    return {
        title: 'Not available live',
        variant: 'error',
        reason: gap.reason,
        guidance: 'This will not change by waiting or retrying. The strategy can still be backtested and researched.',
        shouldRecheck: false,
        requiresRepublish: false
    };
};
